import { isDebugMode, debugLog, debugError, debugWarn } from '../utils/debug.js';

/**
 * SoulPool - Manages a pool of pre-fetched soul incarnations to prevent game stuttering
 */
class SoulPool {
    constructor(api) {
        this.api = api;
        this.pool = new Map(); // Map of forgeType -> Array of incarnation data
        this.targetPoolSize = 15; // Keep 15 souls ready per forge type
        this.minPoolSize = 5; // Refill when pool drops below 5
        this.refillInProgress = new Set(); // Track which forge types are being refilled
        this.lastRefillTime = 0;
        this.refillCooldown = 2000; // Wait 2 seconds between refill attempts
        this.forgeSessionId = null; // Will be set when forge session is created
        
        // Initialize pools for known forge types
        const forgeTypes = ['combat', 'collaboration', 'creation', 'diplomacy', 'exploration', 'caretaking'];
        forgeTypes.forEach(type => {
            this.pool.set(type, []);
        });
        
        // Don't pre-populate with offline souls anymore
        // Bases will wait for real souls from the backend
        debugLog('[SoulPool] Pool initialized (no pre-population with offline souls)');
    }
    
    
    /**
     * Set the forge session ID for this pool
     */
    setForgeSession(forgeSessionId) {
        this.forgeSessionId = forgeSessionId;
        debugLog(`[SoulPool] Set forge session ID: ${forgeSessionId}`);
    }
    
    /**
     * Pre-populate pool with offline souls for immediate availability
     */
    prePopulatePool(forgeType, count) {
        const typePool = this.pool.get(forgeType);
        if (typePool) {
            for (let i = 0; i < count; i++) {
                typePool.push(this.createOfflineSoul());
            }
            debugLog(`[SoulPool] Pre-populated ${forgeType} pool with ${count} offline souls`);
        }
    }
    
    /**
     * Get a soul from the pool immediately (non-blocking)
     */
    getSoul(forgeType = 'combat', teamPreferences = []) {
        const typePool = this.pool.get(forgeType);
        if (!typePool || typePool.length === 0) {
            debugLog(`[SoulPool] No ${forgeType} souls in pool (pool exists: ${!!typePool}, length: ${typePool ? typePool.length : 'N/A'}), using offline soul`);
            debugLog(`[SoulPool] Pool status:`, this.getStatus());
            return this.createOfflineSoul();
        }
        
        // Find a soul with matching team preference, or just take the first one
        let soulIndex = 0;
        if (teamPreferences.length > 0) {
            soulIndex = typePool.findIndex(soul => 
                soul.team_preferences && soul.team_preferences.some(pref => 
                    teamPreferences.includes(pref)
                )
            );
            if (soulIndex === -1) soulIndex = 0; // No match, take first
        }
        
        const soul = typePool.splice(soulIndex, 1)[0];
        // Only log when pool is getting low
        if (typePool.length <= this.minPoolSize) {
            debugLog(`[SoulPool] Dispensed ${forgeType} soul, pool now has ${typePool.length} souls (getting low)`);
        }
        
        // Trigger refill if pool is getting low
        this.checkAndRefill(forgeType);
        
        return soul;
    }
    
    /**
     * Create offline soul data for fallback
     */
    createOfflineSoul() {
        return {
            soul_id: `offline-soul-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
            incarnation_id: `offline-inc-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
            modifiers: {
                courage_bonus: (Math.random() - 0.5) * 0.2,
                aim_adjustment: (Math.random() - 0.5) * 0.1,
                retreat_timing: (Math.random() - 0.5) * 40
            },
            personality_hints: ["offline soul"],
            vendetta_souls: [],
            team_preferences: [] // Add team preferences that real souls have
        };
    }
    
    /**
     * Check if pool needs refilling and trigger if necessary
     */
    checkAndRefill(forgeType) {
        // Skip if offline mode enabled
        if (this.api.offlineMode) return;

        const typePool = this.pool.get(forgeType);
        const now = Date.now();

        if (typePool && typePool.length < this.minPoolSize &&
            !this.refillInProgress.has(forgeType) &&
            now - this.lastRefillTime > this.refillCooldown) {

            this.refillPool(forgeType);
        }
    }
    
    
    /**
     * Background refill process
     */
    startBackgroundRefill() {
        // Skip background refill if API is in offline mode
        if (this.api.offlineMode) {
            debugLog('[SoulPool] Skipping background refill (offline mode)');
            return;
        }

        // Don't immediately refill combat - we already pre-populated it
        // Just start the background refilling process

        // Start refilling after a short delay to let the game initialize
        setTimeout(() => {
            if (!this.api.offlineMode) {
                this.refillPool('combat');
            }
        }, 100);

        // Continue background refilling every 10 seconds
        setInterval(() => {
            // Skip if offline mode enabled
            if (this.api.offlineMode) return;

            // Refill all pools that are below target
            for (const [forgeType, typePool] of this.pool) {
                if (typePool.length < this.targetPoolSize && !this.refillInProgress.has(forgeType)) {
                    this.refillPool(forgeType);
                }
            }
        }, 10000); // Check every 10 seconds
    }
    
    /**
     * Refill pool for a specific forge type (async, non-blocking)
     */
    async refillPool(forgeType) {
        if (this.refillInProgress.has(forgeType)) return;
        
        this.refillInProgress.add(forgeType);
        this.lastRefillTime = Date.now();
        
        try {
            const typePool = this.pool.get(forgeType);
            const neededSouls = this.targetPoolSize - typePool.length;

            debugLog(`[SoulPool] Refilling ${forgeType} pool (need ${neededSouls} souls)`);

            // Request multiple souls in parallel, but limit to 5 at a time
            const batchSize = Math.min(neededSouls, 5);
            const promises = [];
            
            for (let i = 0; i < batchSize; i++) {
                promises.push(this.api.requestIncarnation(forgeType, []));
            }
            
            const souls = await Promise.all(promises);
            
            // Add successful souls to pool
            souls.forEach(soul => {
                if (soul && soul.soul_id) {
                    typePool.push(soul);
                }
            });

            debugLog(`[SoulPool] Refilled ${forgeType} pool, now has ${typePool.length} souls`);

        } catch (error) {
            console.error(`[SoulPool] Failed to refill ${forgeType} pool:`, error);
            console.error(`[SoulPool] Error details:`, {
                message: error.message,
                stack: error.stack,
                apiUrl: this.api.baseURL
            });
        } finally {
            this.refillInProgress.delete(forgeType);
        }
    }
    
    /**
     * Get pool status for debugging
     */
    getStatus() {
        const status = {};
        for (const [forgeType, typePool] of this.pool) {
            status[forgeType] = {
                count: typePool.length,
                refilling: this.refillInProgress.has(forgeType)
            };
        }
        return status;
    }
}

/**
 * SoulforgeAPI - Handles communication with the Soulsoup backend
 * Manages soul incarnations and event streaming
 */
export class SoulforgeAPI {
    constructor(baseURL = 'https://api.animarium.app') {
        this.baseURL = baseURL;
        this.incarnations = new Map(); // Maps entity ID to incarnation data
        this.eventQueue = [];
        this.batchInterval = 5000; // Send events every 5 seconds
        this.heartbeatInterval = 30000; // Send heartbeat every 30 seconds
        this.gameSessionId = this.generateSessionId();
        this.forgeSessionId = null; // Will be set when forge session is created
        this.useOfflineSoulsAsFallback = true; // Can be disabled for production

        // Offline mode detection and connection management
        this.offlineMode = false; // Will be set to true if API is unreachable
        this.connectionAttempts = 0;
        this.maxConnectionAttempts = 3; // Stop trying after 3 failures
        this.lastConnectionAttempt = 0;
        this.connectionRetryDelay = 5000; // Wait 5 seconds between retries
        this.isLocalhost = window.location.hostname === 'localhost' ||
                          window.location.hostname === '127.0.0.1' ||
                          window.location.hostname.includes('jeremedia.com');

        // Initialize soul pool for non-blocking soul requests
        this.soulPool = new SoulPool(this);

        // Only attempt API connection if not localhost or if explicitly enabled
        if (this.isLocalhost) {
            debugLog('[SoulforgeAPI] Running in localhost mode - skipping API connection');
            debugLog('[SoulforgeAPI] Using offline souls only');
            this.offlineMode = true;
            return;
        }

        // Auto-create forge session for this game
        this.initializeForgeSession();

        // Start event batching
        this.startEventBatching();

        // Start heartbeat
        this.startHeartbeat();

        // Start background refill to fetch real souls from backend
        debugLog('[SoulforgeAPI] Starting background refill...');
        this.soulPool.startBackgroundRefill();
        debugLog('[SoulforgeAPI] Soul pool initialization complete');
        debugLog('[SoulforgeAPI] Final pool status:', this.soulPool.getStatus());
    }
    
    generateSessionId() {
        return `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }
    
    /**
     * Initialize a forge session for this game automatically
     * This creates backend session tracking for all incarnations
     */
    async initializeForgeSession() {
        try {
            debugLog('[SoulforgeAPI] Creating forge session...');
            
            const response = await fetch(`${this.baseURL}/api/v1/forge_sessions`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    forge_type: 'combat',
                    game_session_id: this.gameSessionId,
                    settings: {
                        min_participants: 1,
                        max_duration: 1800, // 30 minutes
                        victory_condition: 'elimination',
                        team_balance: 'auto'
                    }
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            this.forgeSessionId = data.forge_session_id;
            this.forgeSessionDbId = data.session_id; // Store the database ID (returned as session_id)
            
            debugLog('[SoulforgeAPI] Forge session created:', {
                forgeSessionId: this.forgeSessionId,
                forgeSessionDbId: this.forgeSessionDbId,
                teams: data.teams,
                maxParticipants: data.max_participants,
                settings: data.settings
            });
            
            // Notify soul pool about the forge session
            this.soulPool.setForgeSession(this.forgeSessionId);
            
        } catch (error) {
            console.error('[SoulforgeAPI] Failed to create forge session:', error);
            // Continue without forge session - incarnations will work but won't be tracked
        }
    }
    
    /**
     * Get a soul incarnation immediately from the pool (non-blocking)
     * This is the preferred method for game entities to avoid stuttering
     */
    getIncarnationFromPool(forgeType = 'combat', teamPreferences = []) {
        return this.soulPool.getSoul(forgeType, teamPreferences);
    }
    
    /**
     * Get soul pool status for debugging
     */
    getSoulPoolStatus() {
        return this.soulPool.getStatus();
    }
    
    /**
     * Manually refill soul pool for debugging
     */
    refillSoulPool(forgeType = 'combat') {
        debugLog(`[SoulAPI] Manually refilling ${forgeType} pool...`);
        return this.soulPool.refillPool(forgeType);
    }
    
    /**
     * Request a soul incarnation for a new entity
     * NOTE: This is now primarily used by the SoulPool for background fetching
     */
    async requestIncarnation(forgeType = 'combat', teamPreferences = []) {
        // Skip API call if in offline mode
        if (this.offlineMode) {
            return this.soulPool.createOfflineSoul();
        }

        try {
            // debugLog(`[SoulAPI] Requesting soul incarnation for ${forgeType} forge...`);  // Too spammy

            const requestBody = {
                game_session_id: this.gameSessionId,
                forge_type: forgeType,
                team_preferences: teamPreferences,
                challenge_level: 'normal'
            };
            
            // Include forge session ID if we have one
            if (this.forgeSessionId) {
                requestBody.forge_session_id = this.forgeSessionId;
            }
            
            const response = await fetch(`${this.baseURL}/api/v1/incarnations`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(requestBody),
                // Add timeout to prevent hanging
                signal: AbortSignal.timeout(5000)
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                // Only log non-422 errors (422 = soul already has active incarnation, which is expected)
                if (response.status !== 422) {
                    console.error('Soul request failed:', response.status, errorData);
                } else {
                    debugLog('Soul already has active incarnation, using offline soul');
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            debugLog('Soul incarnated:', data.soul_id);
            return data;
        } catch (error) {
            if (this.useOfflineSoulsAsFallback) {
                debugWarn('Failed to request incarnation, using offline soul:', error.message);
                // Return default incarnation data on error
                return {
                    soul_id: `offline-soul-${Date.now()}`,
                    incarnation_id: `offline-inc-${Date.now()}`,
                    modifiers: {},
                    personality_hints: [],
                    vendetta_souls: []
                };
            } else {
                // Re-throw error to let caller handle it (e.g., bases waiting for real souls)
                throw error;
            }
        }
    }
    
    /**
     * Register an incarnation with an entity
     */
    registerIncarnation(entityId, incarnationData) {
        this.incarnations.set(entityId, incarnationData);
    }
    
    /**
     * Get incarnation data for an entity
     */
    getIncarnation(entityId) {
        return this.incarnations.get(entityId);
    }
    
    /**
     * Log a game event for a soul
     */
    logEvent(entityId, eventType, eventData = {}) {
        const incarnation = this.incarnations.get(entityId);
        if (!incarnation) {
            debugWarn(`No incarnation found for entity ${entityId}`);
            return;
        }

        // Skip logging for offline incarnations
        if (incarnation.incarnation_id.startsWith('offline-')) {
            return;
        }

        const event = {
            incarnation_id: incarnation.incarnation_id,
            type: eventType,
            timestamp: Date.now(),
            ...eventData
        };

        this.eventQueue.push(event);
    }
    
    /**
     * Log a combat kill event
     */
    logCombatKill(killerId, victimId, context = {}) {
        const killerInc = this.incarnations.get(killerId);
        const victimInc = this.incarnations.get(victimId);

        if (!killerInc || !victimInc) return;

        // Skip if either participant is an offline incarnation
        if (killerInc.incarnation_id.startsWith('offline-') ||
            victimInc.incarnation_id.startsWith('offline-')) {
            return;
        }

        this.logEvent(killerId, 'combat_kill', {
            target_soul_id: victimInc.soul_id,
            victim_incarnation_id: victimInc.incarnation_id,
            context: context
        });

        this.logEvent(victimId, 'combat_death', {
            killer_incarnation_id: killerInc.incarnation_id,
            killer_soul_id: killerInc.soul_id,
            context: context
        });
    }
    
    /**
     * Log resource gathering
     */
    logResourceGathered(entityId, resourceType, amount = 1) {
        this.logEvent(entityId, 'resource_gathered', {
            resource_type: resourceType,
            amount: amount
        });
    }
    
    /**
     * Start batching and sending events
     */
    startEventBatching() {
        setInterval(() => {
            // Skip if offline mode enabled
            if (this.offlineMode) return;

            if (this.eventQueue.length > 0) {
                this.sendEventBatch();
            }
        }, this.batchInterval);
    }
    
    /**
     * Send queued events to the server
     */
    async sendEventBatch() {
        const events = [...this.eventQueue];
        this.eventQueue = [];
        
        try {
            const response = await fetch(`${this.baseURL}/api/v1/events/batch`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ events })
            });
            
            if (!response.ok) {
                // Re-queue events on failure
                this.eventQueue.unshift(...events);
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            if (result.errors && result.errors.length > 0) {
                debugWarn('Event batch had errors:', result.errors);
                result.errors.forEach((error, i) => {
                    debugWarn(`  Event ${i + 1} error:`, error.error, 'for event:', error.event);
                });
            }
        } catch (error) {
            console.error('Failed to send event batch:', error);
            // Events are re-queued on error
        }
    }
    
    /**
     * End an incarnation when an entity is destroyed
     */
    async endIncarnation(entityId, context = {}) {
        const incarnation = this.incarnations.get(entityId);
        if (!incarnation) {
            debugWarn(`No incarnation found for entity ${entityId}`);
            return Promise.resolve(); // Return resolved promise for consistency
        }
        
        // Skip API call for offline incarnations
        if (incarnation.incarnation_id.includes('offline')) {
            debugLog(`[SoulAPI] Skipping end incarnation for offline soul ${incarnation.soul_id}`);
            this.incarnations.delete(entityId);
            return Promise.resolve();
        }

        // Skip API call for offline incarnations
        if (incarnation.incarnation_id.startsWith('offline-')) {
            this.incarnations.delete(entityId);
            return;
        }

        try {
            debugLog(`[SoulAPI] Ending incarnation for soul ${incarnation.soul_id}`);

            // Send incarnation end event to backend
            const response = await fetch(`${this.baseURL}/api/v1/incarnations/${incarnation.incarnation_id}/end`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    ended_at: new Date().toISOString(),
                    memory_summary: {
                        outcome: context.outcome || 'unknown',
                        final_level: context.final_level || 1,
                        kills: context.kills || 0,
                        lifetime: context.lifetime || 0,
                        last_position: context.last_position || null,
                        last_state: context.last_state || null,
                        final_stats: context
                    }
                })
            });
            
            if (!response.ok) {
                console.error('Failed to end incarnation:', response.status);
            } else {
                debugLog(`[SoulAPI] Successfully ended incarnation ${incarnation.incarnation_id}`);
            }
        } catch (error) {
            console.error('Failed to end incarnation:', error);
        } finally {
            // Always remove from local tracking
            this.incarnations.delete(entityId);
        }
    }
    
    /**
     * Apply soul modifiers to entity stats
     */
    applySoulModifiers(entity, incarnationData) {
        const modifiers = incarnationData.modifiers || {};
        
        // Apply combat modifiers
        if (modifiers.courage_bonus) {
            entity.aggressionThreshold = Math.max(0.1, 
                (entity.aggressionThreshold || 0.3) - modifiers.courage_bonus * 0.5);
        }
        
        if (modifiers.aim_adjustment) {
            entity.aimAccuracy = (entity.aimAccuracy || 0.8) + modifiers.aim_adjustment;
        }
        
        if (modifiers.retreat_timing) {
            entity.retreatHealthThreshold = Math.max(0.1,
                (entity.retreatHealthThreshold || 0.3) + modifiers.retreat_timing / 1000);
        }
        
        // Store soul data on entity
        entity.soulId = incarnationData.soul_id;
        entity.incarnationId = incarnationData.incarnation_id;
        entity.vendettaSouls = incarnationData.vendetta_souls || [];
        entity.personalityHints = incarnationData.personality_hints || [];
    }
    
    /**
     * Start sending heartbeats to keep incarnations alive
     */
    startHeartbeat() {
        this.heartbeatTimer = setInterval(() => {
            if (this.incarnations.size > 0) {
                this.sendHeartbeat();
            }
        }, this.heartbeatInterval);
    }
    
    stopHeartbeat() {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
            debugLog('[SoulforgeAPI] Heartbeat stopped');
        }
    }
    
    /**
     * Send heartbeat with active incarnation IDs
     */
    async sendHeartbeat() {
        // Skip if offline mode enabled
        if (this.offlineMode) return;

        const activeIncarnationIds = Array.from(this.incarnations.values())
            .filter(inc => !inc.incarnation_id.startsWith('offline-'))
            .map(inc => inc.incarnation_id);

        if (activeIncarnationIds.length === 0) return;
        
        // Gather team statistics if available
        let teamStats = null;
        if (window.game && window.game.teamStats) {
            teamStats = window.game.teamStats.map(stat => ({
                team: stat.id,
                units_alive: stat.units,
                kills: stat.kills,
                bases_alive: window.game.bases.filter(b => b.alive && b.team.id === stat.id).length
            }));
        }
        
        try {
            const response = await fetch(`${this.baseURL}/api/v1/sessions/${this.gameSessionId}/heartbeat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    incarnation_ids: activeIncarnationIds,
                    forge_session_id: this.forgeSessionId,
                    team_stats: teamStats
                })
            });
            
            if (response.ok) {
                const result = await response.json();
                // Only log heartbeat errors or important updates
                if (result.incarnations_to_end && result.incarnations_to_end.length > 0) {
                    debugLog(`[SoulAPI] Heartbeat: ending ${result.incarnations_to_end.length} incarnations`);
                }
                
                // Handle any incarnations that should be ended
                if (result.incarnations_to_end && result.incarnations_to_end.length > 0) {
                    result.incarnations_to_end.forEach(incarnationId => {
                        // Find and end the incarnation
                        const entityId = this.findEntityByIncarnationId(incarnationId);
                        if (entityId) {
                            this.endIncarnation(entityId, { outcome: 'admin_ended' });
                        }
                    });
                }
            }
        } catch (error) {
            console.error('Failed to send heartbeat:', error);
        }
    }
    
    /**
     * Find entity ID by incarnation ID
     */
    findEntityByIncarnationId(incarnationId) {
        for (const [entityId, incarnation] of this.incarnations) {
            if (incarnation.incarnation_id === incarnationId) {
                return entityId;
            }
        }
        return null;
    }

    async getGameCommentary(winningTeam, teamStats) {
        try {
            console.log('[SoulAPI] Requesting game commentary...');

            const requestBody = {
                game_session_id: this.gameSessionId,
                winning_team: winningTeam,
                team_stats: teamStats,
            };

            const response = await fetch(`${this.baseURL}/api/v1/commentary/game`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
                signal: AbortSignal.timeout(15000) // Longer timeout for generation
            });

            if (!response.ok) {
                // Commentary endpoint may not exist yet - fail silently
                if (response.status === 404) {
                    console.log('[SoulAPI] Commentary endpoint not available (404)');
                } else {
                    const errorData = await response.json().catch(() => ({ message: 'Failed to get commentary' }));
                    console.error('Commentary request failed:', response.status, errorData);
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data.commentary;

        } catch (error) {
            // Only log unexpected errors (404s already logged above)
            if (!error.message.includes('404')) {
                console.error('Failed to request commentary:', error.message);
            }
            return "The battle was fierce, but the archives of this conflict are momentarily unavailable. A moment of silence for the fallen.";
        }
    }
}

// Create singleton instance
export const soulforgeAPI = new SoulforgeAPI();

// Make it globally accessible for debugging and browser event handlers
window.soulforgeAPI = soulforgeAPI;